/**
 * One turn of the conversation, read in the order the platform runs it: what the
 * customer's message settled, what that did to the outcomes, and what UnlikelyAI
 * then told the agent to do about it.
 *
 * This is the half of the platform's job the chat alone never shows. Not only
 * what it blocked, but what it asked the agent to go and find out.
 */
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
} from "lucide-react";
import { cn } from "@unlikelyai-magic/utils";
import type { Briefing } from "../briefing";
import { contradictionSentence } from "../briefing";
import { ChangedNodesList } from "./ChangedNodesList";
import { CollapsibleCard } from "./CollapsibleCard";
import { OutcomeChangesList } from "./OutcomeChangesList";
import { OutcomeChip } from "./OutcomeChip";

const BlockLabel = ({
  children,
  tone = "muted",
}: {
  children: React.ReactNode;
  tone?: "muted" | "amber" | "green" | "red";
}) => {
  const colour = {
    muted: "text-brand-muted",
    amber: "text-amber-700",
    green: "text-green-600",
    red: "text-red-600",
  }[tone];
  return (
    <h3
      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] ${colour}`}
    >
      {children}
    </h3>
  );
};

const Block = ({
  label,
  tone,
  children,
}: {
  label: React.ReactNode;
  tone?: "muted" | "amber" | "green" | "red";
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1.5 border-t border-gray-100 pt-3 first:border-0 first:pt-0">
    <BlockLabel tone={tone}>{label}</BlockLabel>
    {children}
  </div>
);

export const BriefingEmptyState = () => (
  <section className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2.5 rounded-lg border border-dashed border-gray-300 bg-brand-card p-5 text-center">
    <MessageSquare className="h-6 w-6 text-gray-300" aria-hidden={true} />
    <p className="text-sm font-medium text-brand-muted">Select a message</p>
    <p className="max-w-[340px] text-[13px] leading-normal text-brand-muted">
      This panel shows the turn behind that message: what it settled, what it
      changed, and what UnlikelyAI then told the agent to ask next.
    </p>
  </section>
);

const TurnStep = ({
  direction,
  onClick,
  disabled,
}: {
  direction: "previous" | "next";
  onClick: () => void;
  disabled: boolean;
}) => {
  const Icon = direction === "previous" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={`Show the ${direction} turn`}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-unlikely/40",
        disabled
          ? "cursor-not-allowed text-brand-border"
          : "text-brand-muted hover:bg-unlikely/[0.08] hover:text-unlikely"
      )}
    >
      <Icon className="h-4 w-4" aria-hidden={true} />
    </button>
  );
};

/**
 * What the header says when the card is folded, so it is still worth reading.
 */
const summarise = (briefing: Briefing): string => {
  if (briefing.isEvaluating) return "evaluating";
  const parts = [
    briefing.learned.length > 0 ? `${briefing.learned.length} settled` : null,
    briefing.outcomeChanges.length > 0
      ? `${briefing.outcomeChanges.length} ${briefing.outcomeChanges.length === 1 ? "outcome" : "outcomes"} moved`
      : null,
    briefing.contradictions.length > 0 ? "contradiction" : null,
    briefing.askNext ? "asked a question" : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "nothing decided";
};

const EvaluatingBody = () => (
  <div className="flex flex-col gap-3">
    <div className="flex items-center gap-2 text-brand-muted">
      <span
        className="h-3.5 w-3.5 flex-none animate-spin rounded-full border-2 border-brand-muted border-t-transparent motion-reduce:animate-none"
        aria-hidden={true}
      />
      <span className="text-[13px]">
        Working out what the customer just said.
      </span>
    </div>
    <div className="flex flex-col gap-1.5" aria-hidden={true}>
      <span className="h-6 w-full animate-pulse rounded-md bg-brand-background motion-reduce:animate-none" />
      <span className="h-6 w-4/5 animate-pulse rounded-md bg-brand-background motion-reduce:animate-none" />
    </div>
  </div>
);

interface BriefingCardProps {
  briefing: Briefing;
  isExpanded: boolean;
  onToggle: () => void;
  /**
   * Total number of turns in the conversation, for the header count.
   */
  turnCount: number;
  onPreviousTurn?: () => void;
  onNextTurn?: () => void;
}

export const BriefingCard = ({
  briefing,
  isExpanded,
  onToggle,
  turnCount,
  onPreviousTurn,
  onNextTurn,
}: BriefingCardProps) => (
  <CollapsibleCard
    icon={MessageSquare}
    title={`Turn ${briefing.turnNumber}`}
    tone="unlikely"
    grow={true}
    isExpanded={isExpanded}
    onToggle={onToggle}
    bodyClassName="gap-3"
    meta={
      <span className="text-xs text-brand-muted">
        {isExpanded ? `${briefing.turnNumber} of ${turnCount}` : summarise(briefing)}
      </span>
    }
    actions={
      <>
        <TurnStep
          direction="previous"
          onClick={() => onPreviousTurn?.()}
          disabled={!onPreviousTurn}
        />
        <TurnStep
          direction="next"
          onClick={() => onNextTurn?.()}
          disabled={!onNextTurn}
        />
      </>
    }
  >
    {/* Keyed on the turn so a step forward reads as a step, not a flicker. */}
    <div
      key={briefing.turnNumber}
      className="flex flex-col gap-3 duration-200 animate-in fade-in slide-in-from-bottom-1 motion-reduce:animate-none"
    >
      {briefing.isEvaluating ? (
        <EvaluatingBody />
      ) : (
        <>
          <Block label="What the customer settled">
            <ChangedNodesList
              nodes={briefing.learned}
              targetQuestions={briefing.askedFor}
            />
          </Block>

          <Block label="What that changed">
            <OutcomeChangesList changes={briefing.outcomeChanges} />
          </Block>

          {briefing.contradictions.map((contradiction) => (
            <Block
              key={contradiction.question}
              tone="amber"
              label={
                <>
                  <AlertTriangle className="h-3 w-3" aria-hidden={true} />
                  Contradiction
                </>
              }
            >
              <p className="text-[13px] leading-normal">
                {contradictionSentence(contradiction)}
              </p>
            </Block>
          ))}

          <Block label="Told the agent to ask next">
            {briefing.askNext ? (
              <>
                <p className="text-sm leading-normal">
                  &ldquo;{briefing.askNext.question}&rdquo;
                </p>
                {briefing.askNext.waitingOn.length > 0 && (
                  <p className="text-xs text-brand-muted">
                    {briefing.askNext.waitingOn.join(" and ")}{" "}
                    {briefing.askNext.waitingOn.length === 1
                      ? "waits"
                      : "both wait"}{" "}
                    on it.
                  </p>
                )}
                {briefing.alsoAsk.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                    <span className="text-[11px] text-brand-muted">
                      and after that
                    </span>
                    {briefing.alsoAsk.map((question) => (
                      <span
                        key={question}
                        className="rounded-full border border-brand-border bg-brand-background px-2 py-0.5 text-[11px] text-brand-muted"
                      >
                        {question}
                      </span>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="text-[13px] text-brand-muted">
                Nothing left to chase on this turn.
              </p>
            )}
          </Block>


        </>
      )}
    </div>
  </CollapsibleCard>
);
